import { t } from '@shared/helpers/translation';
import {
    convertMillisecondsToMinutes,
    convertToDate,
    formatMinutes,
    getDifferenceInMillisecond,
    getTime,
    isAfter,
    isBefore,
} from '@shared/helpers/datetime/datetime';
import { TransferOption } from '@/api/Transfer/transfer.interfaces';
import {
    TransferValidateDirection,
    TransferValidateType,
    TransferValidateTimeParams,
    TransferValidateDateComparison,
    TransferTrip,
    TransferSearchCondition,
    TransferPickupValidateResponse,
    TransferProductType,
} from './transfer.interfaces';
import {
    FERRY_BUFFER_DROP_OFF,
    FERRY_BUFFER_DROP_OFF_MINIMUM,
    FERRY_BUFFER_PICKUP,
    FERRY_BUFFER_PICKUP_MINIMUM,
    FLIGHT_BUFFER_DROP_OFF,
    FLIGHT_BUFFER_DROP_OFF_MINIMUM,
    FLIGHT_BUFFER_PICKUP,
    FLIGHT_BUFFER_PICKUP_MINIMUM,
    VEHICLE_DURATION_BUFFER,
} from './transfer.consts';
import { getLocaleDayShortMonth } from '@shared/helpers/datetime/datetime.formatters';

function validateTimeCondition(props: TransferValidateTimeParams) {
    const { sourceDate, targetDate, dateComparisonType, minutes, messages } = props;

    const ms =
        dateComparisonType === TransferValidateDateComparison.After
            ? getDifferenceInMillisecond(sourceDate, targetDate, false)
            : getDifferenceInMillisecond(targetDate, sourceDate, false);

    const diff = convertMillisecondsToMinutes(ms);

    const validOrder =
        dateComparisonType === TransferValidateDateComparison.Before
            ? isBefore(sourceDate, targetDate)
            : isAfter(sourceDate, targetDate);

    if ((!validOrder || diff < 0) && messages?.outOfRange) {
        return {
            type: TransferValidateType.Invalid,
            message: messages.outOfRange,
        };
    }

    if (diff < minutes.minimum && messages?.minimum) {
        return {
            type: TransferValidateType.Invalid,
            message: messages.minimum,
        };
    }

    if (diff < minutes.buffer && messages?.tooClose) {
        return {
            type: TransferValidateType.Warning,
            message: messages.tooClose,
        };
    }

    return undefined;
}

interface TransferOptionArrival {
    estimatedArrival: Date;
    remainingMinutes: number;
    // recommendedPickupTime: string;
}

function getTransferOptionArrivalDetails(
    trip: TransferTrip,
    option?: TransferOption
): TransferOptionArrival | null {
    if (trip && trip.request.startTime && option?.Duration) {
        const start = convertToDate(trip.request.startTime);

        const estimatedArrival = new Date(start.getTime() + option.Duration * 60000);
        const arrival = trip.to.date;

        const ms = arrival.getTime() - estimatedArrival.getTime();
        const remainingMinutes = Math.floor(ms / 60000);

        return {
            estimatedArrival,
            remainingMinutes,
        };
    }

    return null;
}

function getTransferBuffers(
    type: TransferProductType,
    direction: TransferValidateDirection,
    defaultVehicleDuration = true
): { buffer: number; minimum: number } {
    const vehicleDuration = defaultVehicleDuration ? VEHICLE_DURATION_BUFFER : 0;

    if (direction === TransferValidateDirection.Pickup) {
        if (type === TransferProductType.Flight) {
            return { buffer: FLIGHT_BUFFER_PICKUP, minimum: FLIGHT_BUFFER_PICKUP_MINIMUM };
        }
        if (type === TransferProductType.Ferry) {
            return { buffer: FERRY_BUFFER_PICKUP, minimum: FERRY_BUFFER_PICKUP_MINIMUM };
        }
    }

    if (direction === TransferValidateDirection.Dropoff) {
        if (type === TransferProductType.Flight) {
            return {
                buffer: vehicleDuration + FLIGHT_BUFFER_DROP_OFF,
                minimum: vehicleDuration + FLIGHT_BUFFER_DROP_OFF_MINIMUM,
            };
        }
        if (type === TransferProductType.Ferry) {
            return {
                buffer: vehicleDuration + FERRY_BUFFER_DROP_OFF,
                minimum: vehicleDuration + FERRY_BUFFER_DROP_OFF_MINIMUM,
            };
        }
    }

    return { buffer: 0, minimum: 0 };
}

export function validateTransferOptionConditions(
    trip: TransferTrip,
    option: TransferOption
): TransferPickupValidateResponse | undefined {
    if (!trip || !option) return;

    const arrival = getTransferOptionArrivalDetails(trip, option);
    if (!arrival) return;

    if (trip.to.productType === TransferProductType.Flight) {
        const direction = TransferValidateDirection.Dropoff;

        const { buffer, minimum } = getTransferBuffers(trip.to.productType, direction, false);

        return validateTimeCondition({
            sourceDate: convertToDate(arrival.estimatedArrival),
            targetDate: trip.to.date,
            dateComparisonType: TransferValidateDateComparison.Before,
            minutes: { buffer, minimum },
            messages: {
                outOfRange: t({
                    key: 'shared.transfer_pickup_time_too_late_airport',
                }),
                minimum: t({ key: 'shared.transfer_pickup_time_too_late_airport' }),
                tooClose: t({
                    key: 'shared.transfer_estimated_arrival_to_airport',
                    values: [
                        getTime(arrival.estimatedArrival),
                        formatMinutes(arrival.remainingMinutes),
                    ],
                }),
            },
        });
    }

    return undefined;
}

export function getTransferSearchConditions(
    trip: TransferTrip
): TransferSearchCondition | undefined {
    if (!trip) return;

    const { from, to } = trip;

    if (to.productType === TransferProductType.Flight) {
        const direction = TransferValidateDirection.Dropoff;

        const { buffer, minimum } = getTransferBuffers(to.productType, direction);

        return {
            validate: (estimatedArrival: Date) =>
                validateTimeCondition({
                    sourceDate: estimatedArrival,
                    targetDate: to.date,
                    dateComparisonType: TransferValidateDateComparison.Before,
                    minutes: { buffer, minimum },
                    messages: {
                        outOfRange: t({
                            key: 'shared.transfer_selected_time_not_valid_for_airport',
                        }),
                        minimum: t({
                            key: 'shared.transfer_selected_time_not_valid_for_airport',
                        }),
                    },
                }),
            description: {
                title: t({
                    key: 'shared.flight_estimated_departure',
                    values: [`${getTime(to.date)}, ${getLocaleDayShortMonth(to.date)}`],
                }),
                text: t({ key: 'shared.transfer_disclaimer_consider_travel_time' }),
            },
            direction,
        };
    } else if (from.productType === TransferProductType.Flight) {
        const direction = TransferValidateDirection.Pickup;

        const { buffer, minimum } = getTransferBuffers(from.productType, direction);

        return {
            validate: (estimatedArrival: Date) =>
                validateTimeCondition({
                    sourceDate: estimatedArrival,
                    targetDate: from.date,
                    dateComparisonType: TransferValidateDateComparison.After,
                    minutes: { buffer, minimum },
                    messages: {
                        outOfRange: t({
                            key: 'shared.transfer_selected_time_not_valid_for_pickup_point',
                        }),
                        minimum: t({
                            key: 'shared.transfer_selected_time_not_valid_for_pickup_point',
                        }),
                        tooClose: t({
                            key: 'shared.transfer_selected_time_might_not_valid_for_pickup_point',
                        }),
                    },
                }),
            description: {
                title: t({
                    key: 'shared.flight_estimated_arrive',
                    values: [`${getTime(from.date)}, ${getLocaleDayShortMonth(from.date)}`],
                }),
                text: t({ key: 'shared.transfer_disclaimer_consider_pickup_point' }),
            },
            direction,
        };
    } else if (from.productType === TransferProductType.Airport) {
        const direction = TransferValidateDirection.Pickup;

        return {
            description: {
                title: t({ key: 'shared.arrival_information' }),
                text: t({ key: 'shared.arrival_information_description' }),
            },
            direction,
        };
    } else if (to.productType === TransferProductType.Airport) {
        const direction = TransferValidateDirection.Dropoff;

        return {
            description: {
                title: t({ key: 'shared.departure_information' }),
                text: t({
                    key: 'shared.departure_information_description',
                }),
            },
            direction,
        };
    }

    return undefined;
}
